import { useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { BottomSheet, Column, Menu, Popover, Sidebar, TextButton, Typography } from 'seagull';
import { AddCircleLiner24, ChevronRight, WideArrowDownward } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStore, useStoreActions } from "../../../contexts";
import { useCreateVendorAccount } from "../../../hooks/useCreateVendorAccount/useCreateVendorAccount";
import { useScrollBorder } from "../../../hooks/useScrollBorder";
import { useSwitchBusiness } from "../../../hooks/useSwitchBusiness";
import { selectors } from "../../../store/state-selectors";
import styles from "./BusinessSwitcher.module.scss";
import { getBusinessTriggerLogoUrl, getBusinessTriggerSubtext } from "./BusinessSwitcher.utils";
import { BusinessSwitcherLogo } from "./BusinessSwitcherLogo";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export const getBusinessSwitcherTID = () => getTestIds();
export const BusinessSwitcher = _ref => {
  var _anchorEl$current;
  let {
    userInfo,
    inSidebar
  } = _ref;
  const {
    BusinessSwitcherTID
  } = useMemo(() => getBusinessSwitcherTID(), []);
  const {
    logBI,
    resetBusinessData,
    setWorksAt
  } = useStoreActions();
  const {
    switchBusiness
  } = useSwitchBusiness();
  const workspaces = userInfo.workspaces() || [];
  const businessId = userInfo.businessId();
  const isClient = useStore(selectors.user.isClient);
  const onCreateVendorAccount = useCreateVendorAccount(() => logBI(18107));
  const [open, setOpen] = useState(false);
  const anchorEl = useRef(null);
  const menuListRef = useRef(null);
  const validWorkspaces = workspaces.filter(workspace => workspace.isCompletelySetupAndDisplayable());
  const hasAVendorAccount = userInfo.hasAVendorAccount();
  const {
    footerClassName
  } = useScrollBorder({
    contentRef: menuListRef,
    enabled: open,
    footerBaseClassName: styles.footerScrollBorder
  }, [open, validWorkspaces]);
  const menuItems = useMemo(() => {
    return validWorkspaces.map((workspace, index) => {
      const selected = workspace.id() === businessId;
      return {
        id: workspace.id(),
        body: workspace.name(),
        subtext: workspace.getBusinessSwitcherItemSubtext(),
        selected,
        testId: BusinessSwitcherTID.Item(index),
        prefixIcon: /*#__PURE__*/_jsx(BusinessSwitcherLogo, {
          testId: BusinessSwitcherTID.MenuItem(index),
          src: workspace.getBusinessSwitcherItemLogoUrl()
        }),
        onClick: () => {
          if (selected) return;
          setOpen(false);
          switchBusiness(workspace.id());
        }
      };
    });
  }, [validWorkspaces, switchBusiness, BusinessSwitcherTID, businessId]);
  const footerItems = useMemo(() => {
    return compact([hasAVendorAccount && {
      variant: 'contextualAction',
      props: {
        testId: BusinessSwitcherTID.ContextualAction(),
        title: 'Create an additional account',
        onClick: () => {
          setOpen(false);
          logBI(11151);
          resetBusinessData();
          setWorksAt();
        },
        icon: /*#__PURE__*/_jsx(AddCircleLiner24, {})
      }
    }, !hasAVendorAccount && {
      variant: 'promotionalAction',
      props: {
        testId: BusinessSwitcherTID.PromotionalAction(),
        title: 'Try Anchor for your business',
        body: 'Send proposals, bill, and get paid.\nAs simple as that.',
        onClick: () => {
          setOpen(false);
          onCreateVendorAccount();
        },
        className: {
          wrapper: styles.promotionalActionWrapper,
          button: styles.promotionalActionButton
        }
      }
    }]);
  }, [logBI, hasAVendorAccount, onCreateVendorAccount, BusinessSwitcherTID, resetBusinessData, setWorksAt, setOpen]);
  function toggleMenu() {
    const wasOpen = open;
    setOpen(prev => !prev);
    if (!wasOpen && isClient) {
      logBI(18106);
    }
  }
  const minWidth = (_anchorEl$current = anchorEl.current) === null || _anchorEl$current === void 0 ? void 0 : _anchorEl$current.offsetWidth;
  return inSidebar ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Sidebar.Item, {
      id: "business-switcher",
      onClick: toggleMenu,
      "data-testid": BusinessSwitcherTID.Button(),
      after: /*#__PURE__*/_jsx(ChevronRight, {}),
      children: userInfo.businessName()
    }), /*#__PURE__*/_jsx(BottomSheet, {
      open: open,
      onClose: () => setOpen(false),
      onOpen: () => setOpen(true),
      children: /*#__PURE__*/_jsx(Menu, {
        testId: BusinessSwitcherTID.Menu(),
        dimensions: {
          width: 'auto'
        },
        items: menuItems,
        footer: {
          items: footerItems
        }
      })
    })]
  }) : /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(TextButton, {
      onClick: toggleMenu,
      "data-testid": BusinessSwitcherTID.Button(),
      prefixIcon: /*#__PURE__*/_jsx(BusinessSwitcherLogo, {
        fullLogo: true,
        testId: BusinessSwitcherTID.Button(),
        src: getBusinessTriggerLogoUrl(userInfo.business())
      }),
      suffixIcon: /*#__PURE__*/_jsx("div", {
        className: cn(styles.arrowIcon, {
          [styles.open]: open
        }),
        children: /*#__PURE__*/_jsx(WideArrowDownward, {})
      }),
      skin: "secondary",
      underline: "none",
      ref: anchorEl,
      children: /*#__PURE__*/_jsxs(Column, {
        alignItems: "start",
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "paragraph1",
          ellipsis: true,
          children: userInfo.businessName()
        }), /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: "secondary",
          ellipsis: true,
          children: getBusinessTriggerSubtext(userInfo.business())
        })]
      })
    }), /*#__PURE__*/_jsx(Popover, {
      open: open,
      onClose: () => setOpen(false),
      anchorEl: anchorEl.current,
      children: /*#__PURE__*/_jsx(Menu, {
        testId: BusinessSwitcherTID.Menu(),
        dimensions: {
          width: 'fit-content',
          minWidth
        },
        classes: {
          menuList: styles.menuList
        },
        items: menuItems,
        footer: {
          items: footerItems,
          className: footerClassName
        },
        menuListRef: menuListRef
      })
    })]
  });
};
export function BusinessSwitcherForSpecificUsers(_ref2) {
  let {
    inSidebar
  } = _ref2;
  const userInfo = useStore(state => state.userInfo);
  const showBusinessSwitcher = useStore(selectors.showBusinessSwitcher);
  if (!userInfo || !showBusinessSwitcher) {
    return null;
  }
  return /*#__PURE__*/_jsx(BusinessSwitcher, {
    userInfo: userInfo,
    inSidebar: inSidebar
  });
}